import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  clearSelectedJob,
  fetchJob,
  inviteFavoriteToJob,
  inviteProfessionalsToJobs,
} from '~/actions';
import { TAB_INDEX } from '~/components/ProfessionalsHub/utils';
import { fireProfessionalSelected, type TProCategory } from '~/posthogs/events';
import { IFavoriteProfessional, useFavoriteProfessionals } from '~/domain/FavoriteProfessionals';
import { IRecentProfessional, useRecentProfessionals } from '~/domain/RecentProfessionals';
import { useRecommendedProfessionals } from '~/domain/RecommendedProfessionals';
import { useIsError, useIsSuccess, useLoading } from '~/hooks';
import { ProfessionTypeEnum } from '~/models';

const tabToTProCategory: Record<number, TProCategory> = {
  [TAB_INDEX.FAVORITES_LIST]: 'Favorite',
  [TAB_INDEX.RECENT_PROFESSIONALS]: 'Worked With',
  [TAB_INDEX.RECOMMENDED_PROS]: 'New to You',
};

interface IProps {
  jobId?: string;
  jobIds?: string[];
  onClosePopup: () => void;
  isRefetchJob?: boolean;
  defaultProfessionals?: string[];
  isOpen?: boolean;
  filterProfession?: ProfessionTypeEnum;
  officeId?: string;
}

/**
 * @param {boolean} isRefetchJob - Flag to determine if the job should be refetched.
 * @param {string} jobId - Job ID to send the invitation to. Ignored if `jobIds` is provided.
 * @param {string[]} jobIds - List of job IDs to send invitations to. Takes precedence over `jobId` if defined.
 * @param {function} onClosePopup - Callback function to close the popup.
 * @param {string[]} defaultProfessionals -  default professionals to be selected by default.
 * @param {boolean} isOpen - Flag to determine if the popup is open.
 * @param {ProfessionTypeEnum} filterProfession - Profession type to filter professionals.
 */
export const useInviteProfessional = ({
  isRefetchJob = true,
  jobId,
  jobIds,
  onClosePopup,
  defaultProfessionals,
  isOpen,
  filterProfession,
  officeId,
}: IProps) => {
  const dispatch = useDispatch();
  const { data: favoriteProfessionals, isLoading: isLoadingFavoriteProfessionals } =
    useFavoriteProfessionals({
      officeId: officeId || '',
      jobId: jobId,
      enabled: !!isOpen,
    });
  const { data: recentProfessionals, isLoading: isLoadingRecentProfessionals } =
    useRecentProfessionals({
      officeId: officeId || '',
      jobId: jobId,
      enabled: !!isOpen,
    });
  const { data: recommendedProfessionals, isLoading: isLoadingRecommendedProfessionals } =
    useRecommendedProfessionals({
      officeId: officeId || '',
      enabled: !!isOpen,
    });

  const isProfessionalAvailable = (isAvailable: boolean | undefined) => {
    return isAvailable ?? true;
  };

  const favoritesListFiltered = useMemo(
    () =>
      (favoriteProfessionals || []).filter((pro: IFavoriteProfessional) =>
        filterProfession
          ? pro.professions?.includes(filterProfession) && isProfessionalAvailable(pro.isAvailable)
          : isProfessionalAvailable(pro.isAvailable),
      ),
    [favoriteProfessionals, filterProfession],
  );

  const recentsListFiltered = useMemo(
    () =>
      (recentProfessionals || []).filter((pro: IRecentProfessional) =>
        filterProfession
          ? pro.professional?.professions?.includes(filterProfession) &&
            isProfessionalAvailable(pro.isAvailable)
          : isProfessionalAvailable(pro.isAvailable),
      ),
    [recentProfessionals, filterProfession],
  );

  const recommendedListFiltered = useMemo(
    () =>
      (recommendedProfessionals || []).filter((pro) =>
        filterProfession
          ? pro.professions?.includes(filterProfession) && isProfessionalAvailable(pro.isAvailable)
          : isProfessionalAvailable(pro.isAvailable),
      ),
    [recommendedProfessionals, filterProfession],
  );

  const { isLoading: isInviting } = useLoading([
    'INVITE_FAVORITE_TO_JOB',
    'INVITE_PROFESSIONALS_TO_JOBS',
  ]);
  const { isLoading: isLoadingJob } = useLoading(['FETCH_JOB_ACTION']);
  const isLoading =
    isLoadingFavoriteProfessionals ||
    isLoadingRecentProfessionals ||
    isLoadingJob ||
    isLoadingRecommendedProfessionals;

  const { clearError, isError } = useIsError(['INVITE_FAVORITE_TO_JOB']);
  const { clearSuccess, isSuccess } = useIsSuccess(['INVITE_FAVORITE_TO_JOB']);

  const [activeTab, setActiveTab] = useState(TAB_INDEX.FAVORITES_LIST);
  const [selectedProfessionals, setSelectedProfessionals] = useState<Set<string>>(new Set());

  const handleClosePopup = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();
      clearError();
      clearSuccess();
      onClosePopup();
      setActiveTab(0);
      setSelectedProfessionals(new Set());
      // Only clear the job if this popup fetched it (isRefetchJob=true).
      // Otherwise the underlying page's job data gets wiped.
      if (isRefetchJob) {
        dispatch(clearSelectedJob());
      }
    },
    [clearError, clearSuccess, onClosePopup, dispatch, isRefetchJob],
  );

  useEffect(() => {
    if (!jobId || !isOpen) {
      return;
    }

    if (isRefetchJob) {
      dispatch(fetchJob({ jobId }));
    }
  }, [dispatch, isRefetchJob, jobId, isOpen]);

  useEffect(() => {
    if (isOpen && defaultProfessionals) {
      setSelectedProfessionals(new Set(defaultProfessionals));
    }
  }, [isOpen, defaultProfessionals]);

  useEffect(() => {
    if (isError) {
      handleClosePopup();
    }
  }, [handleClosePopup, isError]);

  useEffect(() => {
    if (favoritesListFiltered?.length) {
      setActiveTab(TAB_INDEX.FAVORITES_LIST);
    } else if (recentsListFiltered?.length >= 3) {
      setActiveTab(TAB_INDEX.RECENT_PROFESSIONALS);
    } else if (recommendedListFiltered?.length) {
      setActiveTab(TAB_INDEX.RECOMMENDED_PROS);
    }
  }, [favoritesListFiltered, recentsListFiltered, recommendedListFiltered]);

  const onSelectProfessionals = useCallback((ids: string[], forceAdd: boolean = false) => {
    setSelectedProfessionals((prevSelected) => {
      const newSelected = new Set(prevSelected);

      ids.forEach((id) => {
        if (newSelected.has(id) && !forceAdd) {
          newSelected.delete(id);
        } else {
          if (!newSelected.has(id)) {
            fireProfessionalSelected({
              professionalId: id,
              proCategory: tabToTProCategory[activeTab] || 'Favorite',
            });
          }
          newSelected.add(id);
        }
      });

      return newSelected;
    });
  }, [activeTab]);

  const onSentInvitations = useCallback(() => {
    if (jobIds?.length) {
      dispatch(
        inviteProfessionalsToJobs({
          jobIds,
          professionalIds: [...selectedProfessionals],
          isJobsInvite: true,
          officeId,
        }),
      );

      return;
    }

    dispatch(inviteFavoriteToJob(jobId, [...selectedProfessionals], officeId));
  }, [dispatch, jobId, jobIds, selectedProfessionals, officeId]);

  return {
    activeTab,
    isInviting,
    isLoading,
    isSuccess,
    selectedProfessionals,
    onClosePopup: handleClosePopup,
    onSelectProfessionals,
    onSentInvitations,
    onTabChange: setActiveTab,
    favoriteProfessionals: favoritesListFiltered,
    recentProfessionals: recentsListFiltered,
    recommendedProfessionals: recommendedListFiltered,
  };
};
